const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.'

/**
 * Normalized failure payload matching PowerLego-style callers
 * that check `status` / `message` / `response`.
 */
export const apiFail = (error = 'request_failed', extra = {}) => ({
    status: '0',
    message: GENERIC_ERROR_MESSAGE,
    error: String(error),
    response: null,
    ...extra,
})

/** PowerLego responses use status 1 / "1" for success. */
export const isApiSuccess = (res) =>
    res != null && parseInt(String(res.status), 10) === 1

const looksLikeHtml = (text) => {
    const sample = text.trim().slice(0, 200).toLowerCase()
    return (
        sample.startsWith('<!doctype') ||
        sample.startsWith('<html') ||
        sample.includes('<b>fatal error</b>') ||
        sample.includes('parse error') ||
        sample.includes('warning:') ||
        sample.includes('notice:')
    )
}

/**
 * Auth / gateway style: {"error":"401","message":"Authorization failed."}
 * (no PowerLego `status` field)
 */
const isAuthOrGatewayError = (data) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return false
    if (!('error' in data)) return false
    if ('status' in data || 'response' in data) return false
    return true
}

/**
 * Safe fetch + JSON parse. Never throws — always returns JSON-like data
 * or a normalized failure object so UI callers won't crash.
 */
export const requestJson = async (url, options = {}) => {
    try {
        const response = await fetch(url, options)
        const text = await response.text()

        if (!text) {
            if (!response.ok) {
                console.error('API empty error response', { url, status: response.status })
                return apiFail(response.status || 'empty_response')
            }
            return apiFail('empty_response')
        }

        let data
        try {
            data = JSON.parse(text)
        } catch (parseError) {
            console.error('API returned non-JSON response', {
                url,
                status: response.status,
                isHtml: looksLikeHtml(text),
                preview: text.slice(0, 300),
                parseError,
            })
            return apiFail(response.status || 'invalid_json')
        }

        if (!response.ok || isAuthOrGatewayError(data)) {
            console.error('API request error', { url, status: response.status, data })
            return apiFail(data?.error ?? response.status, {
                ...(data?.error_code != null ? { error_code: data.error_code } : {}),
            })
        }

        return data
    } catch (err) {
        console.error('API network/request failure', { url, err })
        return apiFail('network_error')
    }
}
